import logger from '@/services/logger';
import { httpService } from '../common/http.service';
import type { CreateGroup, Group } from '@/api/group/types/group';
import type { SearchGroup } from '@/types/search-group.interface.js';
import type { ApiResponse } from './../common/types/api-response';
import type { InviteUserToGroupPayload } from '@/api/group/types/invite-user-to-group-payload';
import type { GroupInvitationResponsePayload } from '@/api/group/types/group-invitation-response-payload';

export async function getGroups() {
  try {
    const res = await httpService.get<ApiResponse<Group[]>>('group');
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] getGroups error',
      data: {},
      error: err as Error,
    });
    throw err;
  }
}
export async function getGroupsHistory() {
  try {
    const res = await httpService.get<ApiResponse<Group[]>>('group/group-history');
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] getGroupsHistory error',
      data: {},
      error: err as Error,
    });
    throw err;
  }
}

export async function getGroup(groupId: string) {
  try {
    const res = await httpService.get<ApiResponse<Group>>(`group/${groupId}`);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] getGroup error',
      data: { groupId },
      error: err as Error,
    });
    throw err;
  }
}

export async function addGroup(payload: CreateGroup): Promise<Group> {
  try {
    const res = await httpService.post<ApiResponse<Group>>('group', payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] addGroup error',
      // Name and member list are user content and are not logged.
      data: {},
      error: err as Error,
    });
    throw err;
  }
}

export async function exitGroup(payload: { groupId: string }) {
  try {
    const res = await httpService.post<ApiResponse<boolean>>(`group/exit/${payload.groupId}`);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] exitGroup error',
      data: { groupId: payload.groupId },
      error: err as Error,
    });
    throw err;
  }
}

export async function updateGroup(payload: Partial<CreateGroup> & { id: string }) {
  try {
    const res = await httpService.put<ApiResponse<Group>>(`group/${payload.id}`, payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] updateGroup error',
      data: { groupId: payload.id },
      error: err as Error,
    });
    throw err;
  }
}
export async function closeGroup(groupId: string) {
  try {
    const res = await httpService.delete<ApiResponse<unknown>>(`group/${groupId}`);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] closeGroup error',
      data: { groupId },
      error: err as Error,
    });
    throw err;
  }
}

export async function inviteUserToGroup(payload: InviteUserToGroupPayload) {
  try {
    const res = await httpService.post<ApiResponse<unknown>>('group/invite', payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] iviteUserToGroup error',
      data: { groupId: payload.groupId, memberId: payload.memberId },
      error: err as Error,
    });
    throw err;
  }
}

export async function groupInviteResponse(payload: GroupInvitationResponsePayload) {
  try {
    const res = await httpService.post<ApiResponse<unknown>>('group/invite-response', payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] groupInviteResponse error',
      data: { payload },
      error: err as Error,
    });
    throw err;
  }
}

export async function joinInviteLink(payload: { groupId: string }) {
  try {
    const res = await httpService.post<ApiResponse<unknown>>('group/join-invite-link', payload);
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] joinInviteLink error',
      data: { payload },
      error: err as Error,
    });
    throw err;
  }
}

export async function searchGroups(query: string, signal?: AbortSignal) {
  try {
    const res = await httpService.get<ApiResponse<SearchGroup[]>>('group/search', { q: query }, { signal });
    return res.data.data;
  } catch (err) {
    logger.error({
      message: '[GroupApi] searchGroups error',
      data: {},
      error: err as Error,
    });
    throw err;
  }
}
